// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — règlement d'un dispositif (fiche du dispositif) : règles applicables aujourd'hui, règles
// absentes (jamais de valeur inventée), lien vers le Centre de configuration où le règlement se prépare, se simule
// et se valide (préparateur ≠ valideur), et simulation budgétaire sur des hypothèses (rien n'est modifié).
// Routes : GET /api/dispositifs/configuration/reglements/:id, GET /gabarits/:gabarit, POST /reglements/:id/simuler.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Settings2 } from 'lucide-react';
import { Bouton, BandeauInfo } from '../../design/composants';
import { toArray } from './api';
import { Champ, ErreurServeur } from './commun';
import { useSocial, francs, dateLongue } from './apiSocial';
import { useLectureSociale, erreurSociale, Tuiles, TitreSection } from './communSocial';
import { lienCentre } from './communCampagnes';

/** Hypothèses de simulation par type de dispositif (README configuration § 4). */
const HYPOTHESES = {
  PRET: [['demandes', 'Nombre de prêts'], ['montant', 'Montant moyen (F)'], ['duree', 'Durée (mois)']],
  AIDE: [['demandes', 'Nombre d\'aides'], ['montant_moyen', 'Montant moyen (F)']],
  CAMPAGNE: [['inscrits', 'Nombre d\'inscrits'], ['quantite_par_inscrit', 'Quantité par inscrit']],
};
const LIBELLES_BUDGET = {
  encours_total: 'Encours total', mensualite: 'Mensualité', derniere: 'Dernière mensualité', retenue_mensuelle_totale_max: 'Retenue mensuelle totale (maximum)',
  engagement: 'Engagement', budget_annuel: 'Budget annuel', solde: 'Solde du budget', engagement_achat_stock: 'Engagement d\'achat du stock', inscrits: 'Inscrits',
  unites_demandees: 'Unités demandées', cout_total: 'Coût total', subvention_totale: 'Subvention totale', creances_totales: 'Créances totales', reste_a_charge_moyen: 'Reste à charge moyen',
};
const SANS_FRANCS = ['inscrits', 'unites_demandees'];

function SimulationBudget({ dispositif }) {
  const api = useSocial();
  const champs = HYPOTHESES[dispositif.type] || [];
  const [valeurs, setValeurs] = useState({});
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const lancer = async () => {
    setEnCours(true); setErreur(null);
    const h = Object.fromEntries(champs.filter(([k]) => /^\d+$/.test(String(valeurs[k] || '').replace(/\s/g, ''))).map(([k]) => [k, Number(String(valeurs[k]).replace(/\s/g, ''))]));
    try { setResultat(await api.simulerReglement(dispositif.id, Object.keys(h).length ? h : null)); } catch (e) { setErreur(erreurSociale(e)); }
    setEnCours(false);
  };
  const budget = resultat?.budget || {};
  return (
    <div>
      <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Calcul sur le règlement en vigueur ; rien n'est enregistré ni modifié. Laissez un champ vide pour l'hypothèse par défaut du serveur.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        {champs.map(([k, l]) => (
          <Champ key={k} libelle={l}><input className="rh-champ-saisie" inputMode="numeric" value={valeurs[k] || ''} onChange={(e) => setValeurs((v) => ({ ...v, [k]: e.target.value }))} /></Champ>
        ))}
      </div>
      <Bouton Icone={Calculator} disabled={enCours} onClick={lancer}>Simuler le budget</Bouton>
      <div style={{ marginTop: 12 }}><ErreurServeur erreur={erreur} /></div>
      {resultat && (
        <div style={{ marginTop: 12 }}>
          {resultat.calcul_impossible && <BandeauInfo ton="alerte" titre="Calcul impossible">{resultat.calcul_impossible}</BandeauInfo>}
          {Object.keys(budget).length > 0 && (
            <Tuiles tuiles={Object.entries(budget).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => ({
              libelle: LIBELLES_BUDGET[k] || k, valeur: SANS_FRANCS.includes(k) ? String(v) : francs(v),
            }))} />
          )}
          {resultat.hypotheses?.origine && <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>{resultat.hypotheses.origine}</p>}
          {toArray(resultat.anomalies).map((a) => (
            <BandeauInfo key={`${a.code}-${a.message}`} ton={a.gravite === 'BLOQUANT' ? 'danger' : 'alerte'} titre={a.gravite === 'BLOQUANT' ? 'Anomalie bloquante' : 'Avertissement'}>{a.message}</BandeauInfo>
          ))}
          {resultat.quotite?.message && <p className="rh-form-aide" style={{ margin: 0 }}>{resultat.quotite.message}</p>}
        </div>
      )}
    </div>
  );
}

export default function ReglementCarte({ dispositif, peutPreparer }) {
  const api = useSocial();
  const r = useLectureSociale(() => api.reglement(dispositif.id), [api, dispositif.id]);
  const g = useLectureSociale(() => (dispositif.gabarit ? api.gabarit(dispositif.gabarit) : Promise.resolve(null)), [api, dispositif.gabarit]);
  const [simuler, setSimuler] = useState(false);
  const regles = Object.values(r.donnees?.regles || {});
  const libelleRegle = (code) => toArray(g.donnees?.regles).find((x) => x.code === code)?.libelle || code;
  const absentes = toArray(r.donnees?.obligatoires_absentes);
  return (
    <section className="rh-carte">
      <TitreSection action={<Link className="rh-lien" to={lienCentre({ organisation: dispositif.organisation_id, dispositif: dispositif.id })} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}><Settings2 size={15} aria-hidden="true" />Centre de configuration</Link>}>
        Règlement
      </TitreSection>
      {r.erreur && (r.erreur.code === 'REGLEMENT_ABSENT' || r.erreur.statut === 404
        ? <p style={{ margin: 0, fontSize: 14 }}>{r.erreur.message}</p>
        : <ErreurServeur erreur={r.erreur} />)}
      {r.donnees && (
        <>
          {!regles.length && <p style={{ margin: '0 0 8px', fontSize: 14 }}>Aucune règle validée en vigueur au {dateLongue(r.donnees.date)}.</p>}
          {absentes.length > 0 && (
            <BandeauInfo ton="alerte" titre={`${absentes.length} règle(s) obligatoire(s) absente(s)`}>
              {absentes.map(libelleRegle).join(' ; ')}. Le dispositif ne peut être activé tant que son règlement n'est pas complet et validé.
            </BandeauInfo>
          )}
          {regles.length > 0 && (
            <div className="rh-conteneur-tableau">
              <table className="rh-tableau">
                <thead><tr><th>Règle</th><th>Valeur</th><th>Depuis</th></tr></thead>
                <tbody>
                  {regles.map((x) => (
                    <tr key={x.code}>
                      <td>{x.libelle || libelleRegle(x.code)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{x.portee === 'DISPOSITIF' ? 'Tout le dispositif' : x.portee}</span></td>
                      <td>{x.lisible ?? String(x.valeur)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{dateLongue(x.date_debut)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <p className="rh-form-aide" style={{ margin: '12px 0' }}>
        {peutPreparer ? 'Le règlement (montants, plafonds, durées, dates) se prépare, se simule et se soumet dans le Centre de configuration ; une autre personne le valide.' : 'Le règlement se prépare et se valide dans le Centre de configuration par les personnes qui en ont le mandat.'}
      </p>
      {HYPOTHESES[dispositif.type] && (simuler ? <SimulationBudget dispositif={dispositif} /> : <Bouton Icone={Calculator} onClick={() => setSimuler(true)}>Simulation budgétaire</Bouton>)}
    </section>
  );
}
